'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useTranslations, useLocale } from 'next-intl'
import type { ZonedClientFacts } from '@/lib/menu/zonedClientFacts'
import { ItemModal } from './ItemModal'
import { GuidedItemFlow } from './GuidedItemFlow'
import { micrositeOpensGuidedFlow, retailPickerSoldOut, tapOpensItemSheet, type RetailPickerFacts } from '@/lib/microsite/retailLine'
import { SectionHeader } from './SectionHeader'
import { MenuLayoutClassic } from './menu/MenuLayoutClassic'
import { buildMenuItemViews } from '@/lib/microsite/menuItemView'
import type { CartItem } from './types'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import { isItemAvailableNow, type ItemAvailability } from '@/lib/menu/availability'
import { itemHasUnsellableRequiredGroup } from '@/lib/menu/soldOutGroups'
import { type ItemTranslations } from '@/lib/menu/localizeItem'
import { localizedName, type NameTranslations } from '@/lib/menu/localizedName'
import { track } from '@/lib/tracking/storefrontEvents'
import { centeredScrollLeft, cssPx, listScrollTarget } from '@/lib/microsite/stickyCategories'

type Category = {
  id: string
  name: string
  /** Traducciones por locale del nombre de la categoría (Menú → Idiomas). */
  translations?: NameTranslations | null
  color: string | null
  sort_order: number | null
}

type ModifierOption = {
  id: string
  name: string
  price_delta: number
  is_available: boolean | null
}

type ModifierGroup = {
  id: string
  name: string
  required: boolean | null
  min_selections: number | null
  max_selections: number | null
  /** 'normal' | 'removal' | 'allergy_alert'. La lee GuidedItemFlow (vía
   *  classifyOption) para decidir Agregar/Quitar/pregunta — sin ella, TS no
   *  avisaría si algún día la consulta deja de traerla. */
  display_style?: string | null
  modifier_options: ModifierOption[]
}

type Item = {
  id: string
  name: string
  description: string | null
  translations?: ItemTranslations | null
  price: number
  image_url: string | null
  image_focal?: string | null
  is_available: boolean | null
  category_id: string | null
  item_badge?: string | null
  badge_ends_at?: string | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
  /** Producto de mercado (súper). Ausente = plato. */
  is_retail?: boolean
  /** Pizza armable por zonas (mitad/mitad). Solo el ItemModal legado la soporta —
   *  ver el uso de `zoned_build` más abajo, antes de montar el modal. */
  zoned_build?: boolean | null
  item_modifier_groups?: Array<{
    /** Posición del grupo DENTRO de este ítem (definida por el dueño). Sin
     *  ella, buildGuidedSteps cae al orden sin garantías de PostgREST. */
    sort_order?: number | null
    modifier_groups: ModifierGroup | null
  }>
} & RetailPickerFacts

export type MenuSectionProps = {
  categories: Category[]
  items: Item[]
  pricingCfg: PricingConfig
  /** Rejilla precio×tamaño y modo de precio por zona; se reenvía tal cual al modal.
   *  Obligatoria a propósito: si un sitio nuevo la olvidara, la pizza volvería a
   *  mostrar el precio plano del ingrediente sin que nadie lo note. */
  zonedFacts: ZonedClientFacts
  accentColor: string
  /** Para los chips de quick_notes en el modal del ítem. */
  /** TZ del local — para evaluar la ventana de venta (día/hora) del ítem. */
  timezone?: string | null
  onAddToCart: (item: CartItem) => void
  /** Flujo de venta del local ('classic' | 'guided'). La decisión de qué modal
   *  abrir se toma con `usesGuidedFlow`, que además excluye los platos zonados.
   *  Obligatoria a propósito: sin ella un caller nuevo compila y cae en
   *  clásico en silencio — exactamente el fallo mudo que este ajuste evita. */
  salesFlow: string | null
  /** Local de la tienda: el modal de un producto por peso o con variantes pide su
   *  stock EN VIVO con él (Tarea 2b.3). Un plato no lo usa. */
  locationId?: string
  /** Súper online con `online_retail_v2` (PR 4): categoryId → link ya armado
   *  (`storefrontHref`) de la página del pasillo. La portada trae 12 por pasillo y la
   *  categoría activa con link muestra «Ver todo el pasillo». Ausente (todo restaurante):
   *  el markup de siempre, sin cambios. */
  aisleHrefs?: Record<string, string>
  /** Súper online con `online_retail_v2`: itemId → link ya armado (`storefrontHref`) de la
   *  página del producto, SOLO para los productos retail. Con link, la tarjeta abre la página y
   *  «Agregar» es un botón aparte. Ausente (todo restaurante) o sin la clave (todo plato): la
   *  tarjeta de siempre, sin cambios. */
  productHrefs?: Record<string, string>
  /** Tope `sticky` de la barra de categorías. Ausente (todo restaurante): el de siempre,
   *  `calc(var(--ms-nav-h, 56px) + var(--ms-fulfill-h, 0px))`. Con pestañas Restaurante|Mercado
   *  se pasa `STOREFRONT_TABS_STICKY_TOP`. */
  stickyTop?: string
}

function itemAvailability(item: Item): ItemAvailability {
  return {
    available_from: item.available_from,
    available_until: item.available_until,
    available_days: item.available_days,
  }
}

export function MenuSection({
  categories,
  items,
  pricingCfg,
  zonedFacts,
  accentColor,
  timezone,
  onAddToCart,
  salesFlow,
  locationId,
  aisleHrefs,
  productHrefs,
  stickyTop,
}: MenuSectionProps) {
  const t = useTranslations('public.microsite')
  const locale = useLocale() === 'en' ? 'en' : 'es'
  const tz = timezone ?? 'UTC'
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categories[0]?.id ?? null
  )
  const [modalItem, setModalItem] = useState<Item | null>(null)

  const itemsByCategory = useMemo(() => {
    return items.filter((i) => i.category_id === selectedCategory)
  }, [items, selectedCategory])

  // 🔴 El precio se resuelve UNA sola vez, acá. Las presentaciones reciben
  // `cardPrice` ya calculado y no tienen acceso al precio crudo: así ninguna
  // puede pintar el de efectivo en un local con precio dual.
  const views = useMemo(
    () => buildMenuItemViews(itemsByCategory, { pricingCfg, tz, locale }),
    [itemsByCategory, pricingCfg, tz, locale],
  )

  function handleTapById(id: string) {
    const item = items.find((i) => i.id === id)
    if (item) handleCardTap(item)
  }

  function handleCardTap(item: Item) {
    // Agotado (86): visible en la carta pero nunca agregable — el server igual
    // lo rechazaría (409 items_unavailable); aquí evitamos el intento. La regla
    // es la MISMA de la tarjeta (buildMenuItemView.soldOut), incluido el plato
    // que quedó sin ingredientes en un grupo obligatorio: si el sello dice
    // "Agotado", el tap tampoco abre nada.
    if (item.is_available === false || itemHasUnsellableRequiredGroup(item)) return
    // Súper online (R6b): con variantes y ninguna activa, o por peso sin unidad válida,
    // no hay qué elegir — la tarjeta dice «Agotado» (buildMenuItemView) y el toque no
    // abre un selector vacío.
    if (retailPickerSoldOut(item)) return
    // Fuera de su ventana de venta no se puede agregar (el server igual lo
    // rechazaría con 422; aquí evitamos el cobro-y-reverso).
    if (!isItemAvailableNow(itemAvailability(item), tz)) return
    // Plato con modificadores (main) o producto por peso / con variantes (súper
    // online, solo con online_retail_v2): abre el modal en vez de agregar directo.
    if (tapOpensItemSheet(item)) {
      setModalItem(item)
      // Medición de anuncios (no-op sin pixel): «vio el plato» = abrió su detalle.
      const price = effectiveItemCardPrice(item.price, pricingCfg)
      track('view_item', { value: price, currency: 'USD', items: [{ id: item.id, name: item.name, price, quantity: 1 }] })
      return
    }
    // El precio que va al carrito es EL MISMO objeto que pinta la carta: si se
    // recalculara acá, una diferencia entre los dos caminos sería invisible
    // hasta el checkout. El fallback solo cubre un ítem fuera de la categoría
    // visible, que hoy no puede pasar desde la carta.
    const cardPrice =
      views.find((v) => v.id === item.id)?.cardPrice ??
      effectiveItemCardPrice(item.price, pricingCfg)
    onAddToCart({
      // eslint-disable-next-line react-hooks/purity
      cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      itemId: item.id,
      name: item.name,
      price: cardPrice,
      quantity: 1,
      modifiers: [],
    })
  }

  const activeCategory = categories.find((c) => c.id === selectedCategory)
  const activeAisleHref = selectedCategory ? aisleHrefs?.[selectedCategory] : undefined

  // ── Barra de categorías fija (spec 2026-10-19 §4) ──────────────────────────
  // Se pega debajo del header (--ms-nav-h) y de "¿Cómo lo recibes?"
  // (--ms-fulfill-h, 0 si no se muestra) y publica su propio alto como
  // --ms-cats-h para que el scroll al inicio de la lista no deje nada tapado.
  const barRef = useRef<HTMLDivElement>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const pillRefs = useRef(new Map<string, HTMLButtonElement>())
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--ms-cats-h', `${Math.round(bar.offsetHeight)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(bar)
    // Sombra solo cuando está pegada: sin ella se mezcla con las tarjetas al pasar por debajo.
    let raf = 0
    const check = () => {
      raf = 0
      const top = cssPx(getComputedStyle(bar).top)
      setStuck(bar.getBoundingClientRect().top <= top + 0.5 && window.scrollY > 0)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(check)
    }
    check()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      ro?.disconnect()
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
      root.style.removeProperty('--ms-cats-h')
    }
  }, [categories.length])

  function selectCategory(id: string) {
    setSelectedCategory(id)
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const behavior: ScrollBehavior = reduce ? 'auto' : 'smooth'

    // 1) Pestaña activa visible (centrada si se puede) en la fila horizontal.
    const row = rowRef.current
    const pill = pillRefs.current.get(id)
    if (row && pill) {
      row.scrollTo({
        left: centeredScrollLeft(pill.offsetLeft - row.offsetLeft, pill.offsetWidth, row.clientWidth, row.scrollWidth),
        behavior,
      })
    }

    // 2) Inicio de la lista justo debajo de las barras fijas, solo si quedó por
    //    arriba de ellas (si el cliente está arriba, la página no se mueve).
    const list = listRef.current
    const bar = barRef.current
    if (list && bar) {
      const stickyBottom = cssPx(getComputedStyle(bar).top) + bar.offsetHeight
      const target = listScrollTarget(list.getBoundingClientRect().top, window.scrollY, stickyBottom)
      if (target !== null) window.scrollTo({ top: target, behavior })
    }
  }

  return (
    <section id="menu" className="scroll-mt-20 px-5 sm:px-8 pt-8 sm:pt-12 pb-6 max-w-6xl mx-auto">
      <SectionHeader number="01" kicker={t('ourMenu')} title={activeCategory ? localizedName(activeCategory, locale) : ''} />

      {/* Category nav */}
      {categories.length > 0 && (
        <div
          ref={barRef}
          data-testid="menu-category-bar"
          data-stuck={stuck ? 'true' : undefined}
          className="sticky z-20 mt-4 -mx-5 sm:-mx-8 transition-shadow"
          style={{
            top: stickyTop ?? 'calc(var(--ms-nav-h, 56px) + var(--ms-fulfill-h, 0px))',
            background: 'var(--ms-bg)',
            boxShadow: stuck ? '0 6px 12px -10px rgba(0,0,0,0.35)' : 'none',
          }}
        >
        <div ref={rowRef} role="tablist" className="flex gap-2 overflow-x-auto py-2 px-5 sm:px-8 no-scrollbar">
          {categories.map((cat) => {
            const active = cat.id === selectedCategory
            return (
              <button
                key={cat.id}
                ref={(el) => {
                  if (el) pillRefs.current.set(cat.id, el)
                  else pillRefs.current.delete(cat.id)
                }}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectCategory(cat.id)}
                className="shrink-0 rounded-full px-3 py-1.5 text-[13px] font-semibold transition border"
                style={
                  active
                    ? { background: 'var(--ms-text)', color: 'var(--ms-bg)', borderColor: 'var(--ms-text)' }
                    : { background: 'transparent', color: 'var(--ms-text-muted)', borderColor: 'var(--ms-border)' }
                }
              >
                {localizedName(cat, locale)}
              </button>
            )
          })}
        </div>
        </div>
      )}

      <div ref={listRef} className="scroll-mt-0">
      <MenuLayoutClassic
        views={views}
        onTap={handleTapById}
        addLabel={t('add')}
        soldOutLabel={t('soldOut')}
        emptyLabel={t('noItemsInCategory')}
        fromLabel={t('priceFrom')}
        productHrefs={productHrefs}
      />
      {activeAisleHref && (
        <div className="mt-4 sm:mt-6 flex justify-center">
          <a
            href={activeAisleHref}
            data-testid="menu-aisle-link"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold border transition hover:-translate-y-0.5"
            style={{ color: 'var(--ms-accent)', borderColor: 'var(--ms-border)', background: 'var(--ms-surface)' }}
          >
            {t('seeWholeAisle')}
            <span aria-hidden="true">→</span>
          </a>
        </div>
      )}
      </div>

      <AnimatePresence>
        {modalItem && (
          micrositeOpensGuidedFlow(salesFlow, modalItem) ? (
            <GuidedItemFlow
              item={modalItem}
              pricingCfg={pricingCfg}
              accentColor={accentColor}
              onAdd={(cartItem) => {
                onAddToCart(cartItem)
                setModalItem(null)
              }}
              onClose={() => setModalItem(null)}
            />
          ) : (
            <ItemModal
              key={modalItem.id}
              item={modalItem}
              pricingCfg={pricingCfg}
              zonedFacts={zonedFacts}
              accentColor={accentColor}
              locationId={locationId}
              onAdd={(cartItem) => {
                onAddToCart(cartItem)
                setModalItem(null)
              }}
              onClose={() => setModalItem(null)}
            />
          )
        )}
      </AnimatePresence>
    </section>
  )
}
